import React, { useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'components/Luxkit/Typography/Caption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPriceWithCaption from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import getValueOffPercent from 'lib/offer/getValueOffPercent'

interface Props {
  selectedDay: App.CalendarDay;
  offer: App.Offer;
  isAnyFlightsActive?: boolean;
  duration: number;
  isFromPrice?: boolean;
  displayMemberPrice: boolean;
  canViewLuxPlusBenefits: boolean;
}

function OfferCalenderDatePickerFooterPriceDetails(props: Props) {
  const { selectedDay, offer, isAnyFlightsActive, duration, isFromPrice, displayMemberPrice, canViewLuxPlusBenefits } = props
  const canShowPrice = !(offer.bundledWithFlightsOnly && !selectedDay?.flightCacheActive)

  const [price, value, discount] = useMemo(():[number, number, number] => {
    const selectedDayValue = canViewLuxPlusBenefits ? selectedDay?.hotelMemberValue : selectedDay?.hotelValue

    if (selectedDay) {
      const isDiscountPillHidden = offer.isDiscountPillHidden
      const discount = isDiscountPillHidden ? 0 : getValueOffPercent(selectedDayValue, selectedDay.hotelPrice)
      if (isAnyFlightsActive) {
        return [selectedDay.packagedTotal, selectedDayValue + selectedDay.flightPrice, discount]
      }
      return [selectedDay.hotelTotal, selectedDayValue, discount]
    }
    return [0, 0, 0]
  }, [canViewLuxPlusBenefits, selectedDay, offer.isDiscountPillHidden, isAnyFlightsActive])

  const shouldShowDiscountPercent = (discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden
  const durationCaption = pluralizeToString('night', duration)

  const {
    saleUnit,
    total: displayPrice,
    value: displayValue,
  } = useOfferPrice({
    value,
    saleUnit: 'total',
    duration,
    total: price,
  })

  if (!canShowPrice) {
    return <Caption variant="large">
      {durationCaption}
    </Caption>
  }

  return <div>
    <LuxPlusPriceGuard
      showMemberPrice={displayMemberPrice}
      fallbackPrice={
        <PriceRowPriceWithCaption
          size="M"
          caption={`${durationCaption} ${isFromPrice ? 'from' : ''}`}
          price={displayPrice}
          saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        />}
    >
      <PriceRowLuxPlusPriceWithCaption
        caption={`${durationCaption} ${isFromPrice ? 'from' : ''}`}
        memberPrice={displayPrice}
        saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        size="M"
      />
    </LuxPlusPriceGuard>
    <PriceRowTaxesAndFees />
    {value > price && offer.shouldDisplayValue && displayValue && <PriceRowValueDiscountWithCaption
      size="M"
      originalValue={displayValue}
      discountPercentage={!isAnyFlightsActive && shouldShowDiscountPercent ? discount : undefined}
    />}
  </div>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    displayMemberPrice: state.displayMemberPrice,
    canViewLuxPlusBenefits: checkCanViewLuxPlusBenefits(appState),
  }
})(OfferCalenderDatePickerFooterPriceDetails)
